'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

const ADMIN_PASSWORD = '5646';

const features = [
{
  icon: '🧾',
  title: 'Facturation rapide',
  desc: 'Créez des factures professionnelles en quelques secondes avec calcul automatique de TVA et remises.',
  size: 'col-span-1 row-span-1'
},
{
  icon: '📦',
  title: 'Gestion des stocks',
  desc: 'Suivez vos produits en temps réel. Alertes de stock bas, références automatiques, étiquettes imprimables.',
  size: 'col-span-1 row-span-2'
},
{
  icon: '💳',
  title: 'Point de vente',
  desc: 'Caisse rapide avec modes de paiement multiples — espèces, carte, crédit client.',
  size: 'col-span-1 row-span-1'
},
{
  icon: '🏭',
  title: 'Module Dépôt complet',
  desc: 'Gérez votre entrepôt central : achats fournisseurs, stock dépôt, bons de transfert signables et analytiques dédiées.',
  size: 'col-span-2 row-span-1'
},
{
  icon: '💰',
  title: 'Module Finance',
  desc: 'Grand livre, capital, flux de trésorerie, prédiction financière et rapports comptables complets.',
  size: 'col-span-1 row-span-1'
},
{
  icon: '🔒',
  title: 'Permissions granulaires',
  desc: 'Contrôle fin par module et sous-module : chaque utilisateur voit exactement ce qu\'il doit voir.',
  size: 'col-span-1 row-span-1'
}];


const stats = [
{ value: '10x', label: 'Plus rapide que la saisie manuelle' },
{ value: '100%', label: 'Données sécurisées dans le cloud' },
{ value: '24/7', label: 'Accès depuis n\'importe quel appareil' },
{ value: '0$', label: 'Frais cachés ou commissions' }];


export default function PresentationPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showAdminPopup, setShowAdminPopup] = useState(false);
  const [adminPwd, setAdminPwd] = useState('');
  const [adminPwdError, setAdminPwdError] = useState(false);
  const [currentYear, setCurrentYear] = useState(2024);

  useEffect(() => {
    setMounted(true);
    setCurrentYear(new Date()?.getFullYear());
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleAdminAccess = () => {
    if (adminPwd === ADMIN_PASSWORD) {
      setShowAdminPopup(false);
      setAdminPwd('');
      setAdminPwdError(false);
      window.location.href = '/super-admin/login';
    } else {
      setAdminPwdError(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans overflow-x-hidden" suppressHydrationWarning>
      {/* Admin Password Popup — always in DOM, shown via state after mount */}
      {showAdminPopup && mounted &&
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-[#1B4FD8]/10 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-[#1B4FD8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-[#0F172A] text-base">Accès administrateur</h3>
                <p className="text-xs text-[#64748B]">Entrez le mot de passe pour continuer</p>
              </div>
            </div>
            <input
            type="password"
            value={adminPwd}
            onChange={(e) => {setAdminPwd(e?.target?.value);setAdminPwdError(false);}}
            onKeyDown={(e) => e?.key === 'Enter' && handleAdminAccess()}
            placeholder="Mot de passe"
            autoFocus
            className={`w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 mb-1 ${
            adminPwdError ?
            'border-red-400 focus:ring-red-300 bg-red-50' : 'border-[#E2E8F0] focus:ring-[#1B4FD8]/30 focus:border-[#1B4FD8]'}`
            } />

            {adminPwdError &&
          <p className="text-xs text-red-500 mb-3">Mot de passe incorrect. Réessayez.</p>
          }
            {!adminPwdError && <div className="mb-3" />}
            <div className="flex gap-2">
              <button
              onClick={() => {setShowAdminPopup(false);setAdminPwd('');setAdminPwdError(false);}}
              className="flex-1 py-2.5 border border-[#E2E8F0] rounded-xl text-sm font-medium text-[#64748B] hover:bg-[#F8FAFC] transition-colors">

                Annuler
              </button>
              <button
              onClick={handleAdminAccess}
              className="flex-1 py-2.5 bg-[#1B4FD8] text-white rounded-xl text-sm font-semibold hover:bg-[#1640b0] transition-colors">

                Accéder
              </button>
            </div>
          </div>
        </div>
      }
      {/* Navbar */}
      <nav
        suppressHydrationWarning
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        mounted && scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm border-b border-[#E2E8F0]' : 'bg-transparent'}`
        }>

        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Image
              src="/assets/images/app_logo.png"
              alt="Easy Frame logo"
              width={32}
              height={32}
              className="rounded-lg" />

            <span className="font-bold text-lg text-[#0F172A]">Easy Frame</span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              href="/register-store"
              className="text-sm font-medium text-[#1B4FD8] px-4 py-2 rounded-lg hover:bg-[#EFF6FF] transition-colors">

              Se connecter
            </Link>
            <Link
              href="/register-store"
              className="text-sm font-semibold bg-[#1B4FD8] text-white px-4 py-2 rounded-lg hover:bg-[#1640b0] transition-colors">

              Créer mon compte
            </Link>
          </div>
        </div>
      </nav>
      {/* Hero */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden">
        {/* Background blobs */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#1B4FD8]/10 rounded-full blur-3xl" />
          <div className="absolute top-32 right-10 w-56 h-56 bg-[#F59E0B]/15 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-[#1B4FD8]/8 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 bg-[#EFF6FF] text-[#1B4FD8] text-xs font-semibold px-3 py-1.5 rounded-full mb-6 border border-[#1B4FD8]/20">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1B4FD8] animate-pulse" />
            Logiciel de gestion commerciale
          </span>

          <h1 className="text-5xl md:text-6xl font-extrabold text-[#0F172A] leading-tight tracking-tight mb-6">
            Gérez votre commerce{' '}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#1B4FD8]">sans effort</span>
              <span className="absolute bottom-1 left-0 right-0 h-3 bg-[#F59E0B]/30 -z-0 rounded" />
            </span>
          </h1>

          <p className="text-lg text-[#64748B] max-w-2xl mx-auto mb-10 leading-relaxed">
            Easy Frame centralise votre facturation, vos stocks, vos clients et vos finances dans une seule application — simple, rapide et accessible partout.
          </p>

          {/* Offline/Online badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-3 bg-white border border-[#E2E8F0] rounded-2xl px-5 py-3 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" />
                <span className="text-xs font-semibold text-[#16A34A]">En ligne</span>
              </div>
              <span className="text-[#CBD5E1] text-sm">|</span>
              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-[#64748B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M6.343 17.657a9 9 0 010-12.728M9.172 15.536a5 5 0 010-7.072M12 12h.01" />
                </svg>
                <span className="text-xs font-semibold text-[#64748B]">Hors ligne</span>
              </div>
              <span className="text-[#CBD5E1] text-sm">→</span>
              <span className="text-xs text-[#0F172A] font-medium">Fonctionne dans les deux cas ✓</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              href="/register-store"
              className="inline-flex items-center justify-center gap-2 bg-[#1B4FD8] text-white font-semibold px-7 py-3.5 rounded-xl hover:bg-[#1640b0] transition-all shadow-lg shadow-[#1B4FD8]/25 hover:shadow-[#1B4FD8]/40 hover:-translate-y-0.5">

              Démarrer maintenant
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
      {/* Stats bar */}
      <section className="max-w-5xl mx-auto px-6 mb-20">
        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-[#E2E8F0] overflow-hidden">
          {stats?.map((s) =>
          <div key={s?.label} className="px-6 py-6 text-center">
              <div className="text-3xl font-extrabold text-[#1B4FD8] mb-1">{s?.value}</div>
              <div className="text-xs text-[#64748B] leading-snug">{s?.label}</div>
            </div>
          )}
        </div>
      </section>
      {/* Features bento grid */}
      <section className="max-w-5xl mx-auto px-6 mb-24">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-3">Tout ce dont vous avez besoin</h2>
          <p className="text-[#64748B] max-w-xl mx-auto">
            Une suite complète d'outils conçus pour les commerçants, boutiques et PME.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[160px]">
          {/* Card 1 — normal */}
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 flex flex-col justify-between hover:shadow-md hover:-translate-y-0.5 transition-all">
            <span className="text-3xl">{features?.[0]?.icon}</span>
            <div>
              <h3 className="font-bold text-[#0F172A] mb-1">{features?.[0]?.title}</h3>
              <p className="text-xs text-[#64748B] leading-relaxed">{features?.[0]?.desc}</p>
            </div>
          </div>

          {/* Card 2 — tall (row-span-2) */}
          <div className="bg-[#1B4FD8] rounded-2xl p-6 flex flex-col justify-between hover:shadow-lg hover:-translate-y-0.5 transition-all row-span-2">
            <span className="text-4xl">{features?.[1]?.icon}</span>
            <div>
              <h3 className="font-bold text-white text-lg mb-2">{features?.[1]?.title}</h3>
              <p className="text-sm text-blue-200 leading-relaxed">{features?.[1]?.desc}</p>
            </div>
            <div className="mt-4 bg-white/10 rounded-xl p-3 text-xs text-blue-100">
              ✓ Alertes de stock bas automatiques<br />
              ✓ Références produits configurables<br />
              ✓ Étiquettes imprimables
            </div>
          </div>

          {/* Card 3 — normal */}
          <div className="bg-[#F59E0B]/10 rounded-2xl border border-[#F59E0B]/20 p-6 flex flex-col justify-between hover:shadow-md hover:-translate-y-0.5 transition-all">
            <span className="text-3xl">{features?.[2]?.icon}</span>
            <div>
              <h3 className="font-bold text-[#0F172A] mb-1">{features?.[2]?.title}</h3>
              <p className="text-xs text-[#64748B] leading-relaxed">{features?.[2]?.desc}</p>
            </div>
          </div>

          {/* Card 4 — wide (col-span-2) */}
          <div className="md:col-span-2 bg-[#0F172A] rounded-2xl p-6 flex flex-col justify-between hover:shadow-lg hover:-translate-y-0.5 transition-all">
            <span className="text-3xl">{features?.[3]?.icon}</span>
            <div className="flex items-end justify-between gap-4">
              <div>
                <h3 className="font-bold text-white text-lg mb-1">{features?.[3]?.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed max-w-sm">{features?.[3]?.desc}</p>
              </div>
              <div className="flex-shrink-0 bg-white/5 rounded-xl px-4 py-3 text-center">
                <div className="text-2xl font-bold text-[#F59E0B]">✍️</div>
                <div className="text-xs text-slate-400">Bons signables</div>
              </div>
            </div>
          </div>

          {/* Card 5 */}
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 flex flex-col justify-between hover:shadow-md hover:-translate-y-0.5 transition-all">
            <span className="text-3xl">{features?.[4]?.icon}</span>
            <div>
              <h3 className="font-bold text-[#0F172A] mb-1">{features?.[4]?.title}</h3>
              <p className="text-xs text-[#64748B] leading-relaxed">{features?.[4]?.desc}</p>
            </div>
          </div>

          {/* Card 6 */}
          <div className="bg-[#EFF6FF] rounded-2xl border border-[#1B4FD8]/15 p-6 flex flex-col justify-between hover:shadow-md hover:-translate-y-0.5 transition-all">
            <span className="text-3xl">{features?.[5]?.icon}</span>
            <div>
              <h3 className="font-bold text-[#0F172A] mb-1">{features?.[5]?.title}</h3>
              <p className="text-xs text-[#64748B] leading-relaxed">{features?.[5]?.desc}</p>
            </div>
          </div>
        </div>
      </section>
      {/* New Features Highlight Section */}
      <section className="max-w-5xl mx-auto px-6 mb-24">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 bg-[#EFF6FF] text-[#1B4FD8] text-xs font-semibold px-3 py-1.5 rounded-full mb-4 border border-[#1B4FD8]/20">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1B4FD8] animate-pulse" />
            Nouvelles fonctionnalités
          </span>
          <h2 className="text-3xl font-bold text-[#0F172A] mb-3">Des outils avancés pour votre croissance</h2>
          <p className="text-[#64748B] max-w-xl mx-auto">
            Easy Frame évolue constamment. Découvrez les dernières fonctionnalités ajoutées pour optimiser votre gestion.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            {
              icon: '💰',
              color: 'bg-emerald-50 border-emerald-100',
              iconBg: 'bg-emerald-100',
              title: 'Module Finance complet',
              items: ['Grand livre avec soldes journaliers', 'Gestion du capital & apports', 'Flux de trésorerie en temps réel', 'Export PDF & Excel du grand livre'],
            },
            {
              icon: '🏭',
              color: 'bg-[#EFF6FF] border-[#1B4FD8]/15',
              iconBg: 'bg-[#1B4FD8]/10',
              title: 'Module Dépôt complet',
              items: ['Achats fournisseurs au dépôt', 'Gestion du stock central', 'Bons de transfert signables', 'Analytiques dépôt dédiées'],
            },
            {
              icon: '🔮',
              color: 'bg-purple-50 border-purple-100',
              iconBg: 'bg-purple-100',
              title: 'Prédiction des besoins en stock',
              items: ['Analyse 6 mois d\'historique', 'Quantité à réapprovisionner', 'Date suggérée de commande', 'Niveau de confiance (Élevée/Moyenne/Faible)'],
            },
            {
              icon: '🛒',
              color: 'bg-violet-50 border-violet-100',
              iconBg: 'bg-violet-100',
              title: 'Vente Hors Stock (VHS)',
              items: ['Vendre des produits non en stock', 'Facture VHS imprimable (A4/80mm/58mm)', 'Rattachée au point de vente du vendeur', 'Visible dans "Mes ventes du jour"'],
            },
            {
              icon: '👥',
              color: 'bg-teal-50 border-teal-100',
              iconBg: 'bg-teal-100',
              title: 'Clients Vendeur (session caissier)',
              items: ['Bouton Clients dans la session vendeur', 'Ajout & consultation de ses propres clients', 'Encaissements limités au point de vente', 'Accès rapide depuis la sidebar vendeur'],
            },
            {
              icon: '🔒',
              color: 'bg-slate-50 border-slate-200',
              iconBg: 'bg-slate-100',
              title: 'Permissions granulaires',
              items: ['Contrôle par module et sous-module', 'Accès personnalisé par utilisateur', 'Restrictions sur pièces comptables', 'Gestion des rôles avancée'],
            },
            {
              icon: '🏪',
              color: 'bg-amber-50 border-amber-100',
              iconBg: 'bg-amber-100',
              title: 'Mon Stock Vendeur (POS)',
              items: ['Vue stock par catégorie au POS', 'Filtres catégorie & sous-catégorie', 'Mouvements par produit', 'Analyse des produits les plus vendus'],
            },
            {
              icon: '💜',
              color: 'bg-violet-50 border-violet-100',
              iconBg: 'bg-violet-100',
              title: 'Paiement Prépayé',
              items: ['Compte prépayé par client (USD & CDF)', 'Recharge depuis la fiche client', 'Paiement POS déduit du solde prépayé', 'Historique recharges & consommations'],
            },
            {
              icon: '📡',
              color: 'bg-[#F8FAFC] border-[#E2E8F0]',
              iconBg: 'bg-[#16A34A]/10',
              title: 'Mode hors-ligne amélioré',
              items: ['Indicateur connexion permanent', 'File d\'attente de synchronisation', 'POS fonctionnel sans internet', 'Installation PWA sur mobile'],
            },
          ]?.map((card) => (
            <div key={card?.title} className={`rounded-2xl border p-5 ${card?.color} hover:shadow-md hover:-translate-y-0.5 transition-all`}>
              <div className={`w-10 h-10 ${card?.iconBg} rounded-xl flex items-center justify-center mb-3 text-xl`}>
                {card?.icon}
              </div>
              <h3 className="font-bold text-[#0F172A] text-sm mb-3">{card?.title}</h3>
              <ul className="space-y-1.5">
                {card?.items?.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs text-[#64748B]">
                    <span className="text-[#1B4FD8] mt-0.5 shrink-0">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
      {/* Why Easy Frame */}
      <section className="bg-white border-y border-[#E2E8F0] py-20 px-6 mb-24">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0F172A] mb-4">
                Conçu pour les commerçants africains
              </h2>
              <p className="text-[#64748B] mb-6 leading-relaxed">
                Easy Frame est pensé pour la réalité du terrain : multi-devises, gestion des crédits clients, taux de change personnalisables, et une interface disponible en français et en anglais.
              </p>
              <ul className="space-y-3">
                {[
                'Multi-devises avec taux de change configurables',
                'Gestion des crédits et dettes clients',
                'Module Finance : grand livre, capital, flux de trésorerie',
                'Module dépôt complet avec bons de transfert signables',
                'Prédiction IA des besoins en stock par emplacement',
                'Vente Hors Stock (VHS) : vendez sans stock disponible',
                'Clients Vendeur : gestion des clients depuis la session caissier',
                'Paiement Prépayé : compte prépayé par client en USD et CDF',
                'Permissions granulaires par module et sous-module',
                'Mon Stock Vendeur : vue stock par catégorie au POS',
                'Interface bilingue français / anglais',
                'Fonctionne sur mobile, tablette et ordinateur',
                'Fonctionne avec ou sans connexion internet',
                'Données sauvegardées et exportables']?.
                map((item) =>
                <li key={item} className="flex items-start gap-3 text-sm text-[#0F172A]">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[#1B4FD8]/10 flex items-center justify-center">
                      <svg className="w-3 h-3 text-[#1B4FD8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    {item}
                  </li>
                )}
              </ul>
            </div>

            <div className="relative">
              <div className="bg-[#F8FAFC] rounded-2xl border border-[#E2E8F0] p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-[#1B4FD8] flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <span className="font-semibold text-[#0F172A] text-sm">Tableau de bord</span>
                </div>
                <div className="space-y-3">
                  {[
                  { label: 'Chiffre d\'affaires', value: '9 000 000CDF', color: 'bg-[#1B4FD8]', pct: '78%' },
                  { label: 'Produits vendus', value: '342 articles', color: 'bg-[#F59E0B]', pct: '55%' },
                  { label: 'Clients actifs', value: '89 clients', color: 'bg-[#16A34A]', pct: '65%' }]?.
                  map((row) =>
                  <div key={row?.label}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-[#64748B]">{row?.label}</span>
                        <span className="font-semibold text-[#0F172A]">{row?.value}</span>
                      </div>
                      <div className="h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
                        <div className={`h-full ${row?.color} rounded-full`} style={{ width: row?.pct }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#F59E0B] text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-md rotate-2">
                Mis à jour en temps réel
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Pricing Section */}
      <section className="max-w-5xl mx-auto px-6 mb-24">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-[#EFF6FF] text-[#1B4FD8] text-xs font-semibold px-3 py-1.5 rounded-full mb-4 border border-[#1B4FD8]/20">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1B4FD8] animate-pulse" />
            Tarification simple et transparente
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0F172A] mb-3">
            Choisissez votre formule
          </h2>
          <p className="text-[#64748B] max-w-xl mx-auto">
            Toutes les formules incluent le module de gestion de dépôt — sans frais cachés.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-stretch">

          {/* Plan Mensuel */}
          <div className="relative bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-8 flex flex-col hover:shadow-md hover:-translate-y-0.5 transition-all">
            <div className="mb-6">
              <div className="w-10 h-10 bg-[#EFF6FF] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-[#1B4FD8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#0F172A] mb-1">Mensuel</h3>
              <p className="text-xs text-[#64748B]">Flexibilité maximale, sans engagement</p>
            </div>

            <div className="mb-6">
              <div className="flex items-end gap-1 mb-1">
                <span className="text-4xl font-extrabold text-[#0F172A]">29$</span>
                <span className="text-[#64748B] text-sm mb-1.5">/mois</span>
              </div>
              <div className="inline-flex items-center gap-1.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-1.5 mt-1">
                <span className="text-xs font-semibold text-[#1B4FD8]">+ 9$</span>
                <span className="text-xs text-[#64748B]">par point de vente additionnel</span>
              </div>
            </div>

            <ul className="space-y-2.5 mb-8 flex-1">
              {[
              'Logiciel EasyFrame complet',
              'Module gestion de dépôt inclus',
              'Mises à jour incluses',
              'Support client',
              'Points de vente additionnels à 9$/mois']?.
              map((item) =>
              <li key={item} className="flex items-start gap-2.5 text-sm text-[#374151]">
                  <span className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full bg-[#1B4FD8]/10 flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-[#1B4FD8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  {item}
                </li>
              )}
            </ul>

            <Link
              href="/register-store"
              className="block text-center bg-[#F8FAFC] border border-[#E2E8F0] text-[#1B4FD8] font-semibold px-6 py-3 rounded-xl hover:bg-[#EFF6FF] hover:border-[#1B4FD8]/30 transition-all text-sm">

              Commencer à 29$ / mois
            </Link>
          </div>

          {/* Plan 6 Mois — RECOMMANDÉ */}
          <div className="relative bg-gradient-to-b from-[#1B4FD8] to-[#1640b0] rounded-2xl shadow-xl p-8 flex flex-col scale-[1.02] z-10">
            {/* Badge */}
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
              <span className="inline-flex items-center gap-1.5 bg-[#F59E0B] text-[#0F172A] text-xs font-bold px-4 py-1.5 rounded-full shadow-md whitespace-nowrap">
                ⭐ Recommandé
              </span>
            </div>

            <div className="mb-6 mt-2">
              <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">6 Mois</h3>
              <p className="text-xs text-blue-200">Économisez 14$ par rapport au mensuel</p>
            </div>

            <div className="mb-6">
              <div className="flex items-end gap-2 mb-1">
                <span className="text-4xl font-extrabold text-white">160$</span>
                <span className="text-blue-200 text-sm mb-1.5">/6 mois</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-blue-300 line-through text-sm">174$</span>
                <span className="bg-[#F59E0B]/20 text-[#F59E0B] text-xs font-bold px-2 py-0.5 rounded-md border border-[#F59E0B]/30">
                  −8%
                </span>
              </div>
              <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 mt-3">
                <span className="text-xs font-semibold text-[#F59E0B]">+7$</span>
                <span className="text-xs text-blue-200">par point de vente additionnel</span>
              </div>
            </div>

            <ul className="space-y-2.5 mb-8 flex-1">
              {[
              'Logiciel EasyFrame complet',
              'Module gestion de dépôt inclus',
              'Jusqu\'à 5 points de vente',
              'Mises à jour incluses',
              'Support client prioritaire',
              'Facturation semestrielle']?.
              map((item) =>
              <li key={item} className="flex items-start gap-2.5 text-sm text-blue-100">
                  <span className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full bg-[#F59E0B]/20 border border-[#F59E0B]/40 flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-[#F59E0B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  {item}
                </li>
              )}
            </ul>

            <Link
              href="/register-store"
              className="block text-center bg-[#F59E0B] text-[#0F172A] font-bold px-6 py-3 rounded-xl hover:bg-[#d97706] transition-all shadow-lg shadow-black/20 hover:-translate-y-0.5 text-sm">

              Commencer à 160$ / 6 mois
            </Link>
          </div>

          {/* Plan Annuel */}
          <div className="relative bg-[#0F172A] rounded-2xl shadow-sm p-8 flex flex-col hover:shadow-md hover:-translate-y-0.5 transition-all">
            <div className="mb-6">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-[#F59E0B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Annuel</h3>
              <p className="text-xs text-slate-400">Le meilleur rapport qualité-prix</p>
            </div>

            <div className="mb-6">
              <div className="flex items-end gap-1 mb-1">
                <span className="text-4xl font-extrabold text-white">300$</span>
                <span className="text-slate-400 text-sm mb-1.5">/an</span>
              </div>
              <div className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 mt-1">
                <span className="text-xs font-semibold text-[#F59E0B]">+ 5$</span>
                <span className="text-xs text-slate-400">par point de vente additionnel</span>
              </div>
            </div>

            <ul className="space-y-2.5 mb-8 flex-1">
              {[
              'Logiciel EasyFrame complet',
              'Module gestion de dépôt inclus',
              'Points de vente additionnels à 5$/mois',
              'Mises à jour incluses',
              'Support client dédié',
              'Facturation annuelle']?.
              map((item) =>
              <li key={item} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <span className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/30 flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-[#F59E0B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  {item}
                </li>
              )}
            </ul>

            <Link
              href="/register-store"
              className="block text-center bg-white/10 border border-white/20 text-white font-semibold px-6 py-3 rounded-xl hover:bg-white/20 transition-all text-sm">

              Commencer à 300$ / an
            </Link>
          </div>
        </div>

        {/* Depot badge */}
        <div className="mt-8 flex justify-center">
          <div className="inline-flex items-center gap-3 bg-white border border-[#E2E8F0] rounded-2xl px-6 py-4 shadow-sm">
            <div className="w-9 h-9 bg-[#16A34A]/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-[#16A34A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#0F172A]">Module Gestion de Dépôt offert</p>
              <p className="text-xs text-[#64748B]">Inclus gratuitement dans toutes les formules — aucun supplément</p>
            </div>
            <span className="flex-shrink-0 bg-[#16A34A]/10 text-[#16A34A] text-xs font-bold px-2.5 py-1 rounded-lg border border-[#16A34A]/20">
              GRATUIT
            </span>
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 mb-24 text-center">
        <div className="bg-[#0F172A] rounded-3xl px-8 py-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#1B4FD8]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#F59E0B]/15 rounded-full blur-3xl" />
          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Prêt à simplifier votre gestion ?
            </h2>
            <p className="text-slate-400 mb-8 max-w-lg mx-auto">
              Rejoignez les commerçants qui font confiance à Easy Frame pour piloter leur activité au quotidien.
            </p>
            <Link
              href="/register-store"
              className="inline-flex items-center gap-2 bg-[#F59E0B] text-[#0F172A] font-bold px-8 py-4 rounded-xl hover:bg-[#d97706] transition-all shadow-lg shadow-[#F59E0B]/30 hover:-translate-y-0.5">

              Créer mon compte gratuitement
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="border-t border-[#E2E8F0] py-8 px-6">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Image
              src="/assets/images/app_logo.png"
              alt="Easy Frame logo"
              width={24}
              height={24}
              className="rounded" />

            <span className="text-sm font-semibold text-[#0F172A]">Easy Frame</span>
          </div>
          <p className="text-xs text-[#64748B]">
            © {currentYear} Easy Frame. Tous droits réservés.
          </p>
          <p className="text-xs text-[#64748B]">
            Développé par{' '}
            <a
              href="https://www.osil.digital"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1B4FD8] hover:underline font-medium">

              www.osil.digital
            </a>
          </p>
          {/* Hidden admin trigger — triple-click on copyright */}
          <button
            onClick={() => mounted && setShowAdminPopup(true)}
            className="text-xs text-[#94A3B8] hover:text-[#64748B] transition-colors"
            aria-label="Admin access">

            Admin
          </button>
        </div>
      </footer>
    </div>
  );

}